'use client';

import { twBreakpoints } from '@woonstad/design-system';
import { useMediaQuery, useTimeout } from '@woonstad/helpers-hooks';
import { cn, getMediaProperties } from '@woonstad/helpers-utils';
import Image from 'next/image';
import { useState } from 'react';
import clsx from 'clsx';
import { IApiMediaWithCropsModel } from '@woonstad/api-client-content-delivery';

type HeroImageProps = {
  heroImage: IApiMediaWithCropsModel | undefined;
  secondaryHeroImage: IApiMediaWithCropsModel | undefined;
}

export const HeroImage = ({
  heroImage,
  secondaryHeroImage,
}: HeroImageProps) => {
  const isLarge = useMediaQuery(twBreakpoints['min-sm']);
  const [hasOpened, setHasOpened] = useState(false);

  useTimeout(() => {
    setHasOpened(true);
  }, 500);

  const heroImageProps = getMediaProperties(heroImage);
  const secondaryHeroImageProps = getMediaProperties(secondaryHeroImage);

  if (!heroImageProps || !secondaryHeroImageProps || !isLarge) {
    return null;
  }

  return (
    <div className="absolute flex w-[45%] origin-center scale-[0.8] max-sm:top-32 items-center justify-center sm:min-h-[30rem] lg:scale-[1] xl:scale-100">
      {/* Large dark blue dot */}
      <div className={clsx('absolute left-1/2',
        'sm:top-[10%] md:top-[20%] lg:top-[36%]',
        'sm:translate-x-[-12rem] md:translate-x-[-16rem]'
      )}>
        <div
          className={cn(
            'size-[8.5rem] rounded-full bg-[#003CB0] ease-out xl:left-20',
            hasOpened ? 'animate-ball duration-1000' : 'animate-in zoom-in fade-in duration-500',
          )}
          style={{
            animationDuration: hasOpened ? '16s' : '500ms',
          }}
        />
      </div>

      {/* Small light blue dot */}
      <div className="top-1/2 translate-y-[-16rem] lg:translate-y-[-12rem]">

      <div
        className={cn(
          'left-30 absolute size-[2rem] rounded-full bg-[#00C5F2] ease-out',
          hasOpened ? 'animate-ball' : 'animate-in zoom-in fade-in',
        )}
        style={{
          animationDuration: hasOpened ? '12s' : '500ms',
        }}
      />
      </div>

      {/* Small pink dot */}
      <div
        className={cn(
          'absolute -right-10 bottom-10 size-[4rem] rounded-full bg-[#F170B8] ease-out lg:right-4',
          hasOpened ? 'animate-ball' : 'animate-in zoom-in fade-in',
        )}
        style={{
          animationDuration: hasOpened ? '24s' : '500ms',
        }}
      />

      {/* Small orange dot */}
      <div
        className={cn(
          'absolute -right-[28rem] top-20 -z-10 size-[2rem] rounded-full bg-[#FF7842] ease-out md:-right-[36rem] lg:-right-[42rem]',
          hasOpened ? 'animate-ball' : 'animate-in zoom-in fade-in',
        )}
        style={{
          animationDuration: hasOpened ? '24s' : '500ms',
        }}
      />

      {/* Main large image - wrapper for translate, inner for animation */}
      <div className="absolute lg:translate-y-20">
        <div className="animate-in fade-in zoom-in size-[22.5rem] overflow-hidden rounded-full duration-500">
          <Image
            className="size-full object-cover"
            aria-hidden
            src={heroImageProps.url}
            alt={''}
            height={500}
            width={1000}
            sizes={`${twBreakpoints['max-lg']} 90vw, 1000px`}
            priority
          />
        </div>
      </div>

      {/* Second smaller image - wrapper for translate, inner for animation */}
      <div className="absolute -translate-y-24 translate-x-36">
        <div className="animate-in fade-in size-[8.5rem] overflow-hidden rounded-full border-8 border-hero-title-background duration-500">
          <Image
            className="size-full object-cover"
            aria-hidden
            src={secondaryHeroImageProps.url}
            alt={''}
            height={500}
            width={1000}
            sizes={`${twBreakpoints['max-lg']} 90vw, 1000px`}
            priority
          />
        </div>
      </div>
    </div>
  );
};
